import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from '@pearsontechnology/aero-components';
import StyledLink from 'components/StyledLink';
import { PageContent, PageActions } from 'components/PageStyles';
import { ACTIVITY } from 'pluginServices/constants/coursewareTypes';
import AccountHelper from 'Workspace/helpers/AccountHelper';
import { featureKeys } from 'services/FeatureFlagService';

import CSVDownloadButton from '../CSVDownloadButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
  ScrollWrapper,
} from '../Grid/Table';
import AccountSummary from '../Grid/AccountSummary';
import CellValue from '../Grid/CellValue';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

function getActivityProgress(progressByChildActivityId, activityId, accountId) {
  const allActivityProgress = progressByChildActivityId[activityId];
  if (!allActivityProgress) {
    return ' ';
  }

  const accountProgressRow = allActivityProgress.find((data) => data.account.id === accountId);
  if (!accountProgressRow || !(accountProgressRow.progress >= 0)) {
    return '—';
  }

  return accountProgressRow.progress * 100;
}

const NoTitle = 'No title';

function ProgressGrid(props) {
  const { cohortId, deploymentId, activity, childActivities, enrollments, navigationActions, progressByChild, isLoading, getFeatureFlagValue } = props;

  const tableColumns = useMemo(() => [
    'enrollments',
    activity.title,
    ...childActivities.map((childActivity) => childActivity.title),
  ], [activity, childActivities]);

  const tableData = useMemo(() => enrollments.map((enrollment) => [
    enrollment.account,
    {
      element: activity,
      progress: isNaN(enrollment.progress) ? '—' : (enrollment.progress * 100),
    },
    ...childActivities.map((childActivity) => ({
      element: childActivity,
      progress: getActivityProgress(
        progressByChild,
        childActivity.id,
        enrollment.account.id
      ),
    })),
  ]), [activity, childActivities, enrollments, progressByChild]);

  const isNewDesignEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_GRADES_NEW_DESIGN);

  return (
    <React.Fragment>
      <PageActions style={{ overflowX: 'auto', paddingTop: 4, flex: isNewDesignEnabled ? 'none' : undefined }}>
        {navigationActions}
        <CSVDownloadButton
          label="Export to CSV"
          disabled={childActivities.filter(({ id }) => !progressByChild[id]).length > 0}
          fileName={`${activity.title}-progress`}
          columns={tableColumns}
          datas={tableData.map(([account, ...rows]) => ([
            AccountHelper.getFullName(account.identity),
            ...rows.map(({ progress }) => progress),
          ]))}
        />
      </PageActions>
      <PageContent flex100={isNewDesignEnabled}>
        {isLoading && <Spinner />}
        {!isLoading && (
          <ScrollWrapper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell style={{ minWidth: 200, textAlign: 'left', paddingLeft: 16 }}>{tableColumns[1]}</TableHeaderCell>
                  <TableHeaderCell style={{ minWidth: 80 }}>Total</TableHeaderCell>
                  {childActivities.map((childActivity) => (
                    <TableHeaderCell key={childActivity.id} style={{ minWidth: 150 }} >
                      { childActivity.elementType !== ACTIVITY
                        ? (childActivity.title || NoTitle)
                        : (
                          <StyledLink to={`/classes/${cohortId}/grades/${deploymentId}/progress/${childActivity.id}`}>
                            {childActivity.title || NoTitle}
                          </StyledLink>
                        )
                      }
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map(([account, ...rows]) => (
                  <TableRow key={account.id}>
                    <TableHeaderCell>
                      <AccountSummary account={account.identity} />
                    </TableHeaderCell>
                    {rows.map(({ element, progress }) => (
                      <TableCell key={`${account.id}-${element.id}`}>
                        <CellValue unit="%">{progress}</CellValue>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollWrapper>
        )}
      </PageContent>
    </React.Fragment>
  );
}

ProgressGrid.propTypes = {
  isLoading: PropTypes.bool,
  activity: PropTypes.object,
  childActivities: PropTypes.array,
  enrollments: PropTypes.array,
  progressByChild: PropTypes.object,
  cohortId: PropTypes.string,
  deploymentId: PropTypes.string,
  navigationActions: PropTypes.element,
};

ProgressGrid.defaultProps = {
  isLoading: true,
  activity: {},
  childActivities: [],
  enrollments: [],
};

const flagNames = [featureKeys.WORKSPACE_GRADES_NEW_DESIGN];
export default enhanceWithFeatureFlagSubscription(ProgressGrid, flagNames);
